import { useEffect, useState } from "react";
import NotificationsService from "../services/NotificationsService";
import UserAuthService from "../services/UserAuthService";
import { IXSifterNotificationListRequest, IXSifterUser, IXSifterUserNotification, IXSifterUserNotificationList } from "../types/XSifterAppUser"
import React from "react";
import { Grid, Pagination, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Button, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, Select, MenuItem, Switch } from "@mui/material";
import { useNavigate } from "react-router";
import { formatIsoDate, pluralizeCount } from "../types/FormattingHelper";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArchiveIcon from '@mui/icons-material/Archive';
import ClearIcon from '@mui/icons-material/Clear';

import "./XSifterNotificationList.css";
import SortableFieldHeader from "./common/SortableFieldHeader";

interface IXSifterNotificationsProps {
    currentUser: IXSifterUser | undefined,
    onForceHeaderRemount: ()=>void
}

const XSifterNotifications: React.FC<IXSifterNotificationsProps> = (props: IXSifterNotificationsProps) => {
    const userAuthService = UserAuthService.getInstance();
    const [listRequest, setListRequest] = useState<IXSifterNotificationListRequest>({limit: 10, offset: 0});
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [notificationsList, setNotificationsList] = useState<IXSifterUserNotificationList | undefined>(undefined);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkAction, setBulkAction] = useState<string>("");
    const [showArchived, setShowArchived] = useState<boolean>(false);
    const [showUnread, setShowUnread] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        userAuthService.setCurrentUser(props.currentUser);
        fetchNotificationList(listRequest);
    }, []);

    const handlePageChange =(event: React.ChangeEvent<unknown>, value: number) => {
        let newOffset = (value-1) * 10; // 10 is hardcoded limit
        fetchNotificationList({
            ...listRequest,
            offset: newOffset
        });
    }

    const fetchNotificationList = (request: IXSifterNotificationListRequest) => {
        (new NotificationsService()).getNotifications(request)
            .then((response) => {
                setupNotifications(request, response);
            }).catch(() => {});
    }

    const setupNotifications = (request: IXSifterNotificationListRequest, response: IXSifterUserNotificationList) => {
        let totalItems = response.meta.count;
        let itemsPerPage = response.meta.limit;
        let currentOffset = response.meta.offset;
        if (totalItems === 0) {
            setTotalPages(0);
            setPageNumber(0);
        } else {
            let totalPages = Math.ceil(totalItems / itemsPerPage);
            let currentPage = Math.ceil(currentOffset / itemsPerPage) + 1;
            setTotalPages(totalPages);
            setPageNumber(currentPage);
        }
        setListRequest({
            ...request,
            offset: response.meta.offset,
            limit: response.meta.limit
        });
        setNotificationsList(response);
        setSelectedIds(new Set());
    }

    const toggleSelectAll = (checked: boolean) => {
        if (!notificationsList)
            return;

        setSelectedIds(checked ? new Set(notificationsList.items.map(x => x.notificationId)) : new Set());
    }

    const toggleSelectItem = (id: string, checked: boolean) => {
        setSelectedIds(prev => {
            let updated = new Set(prev);
            if (checked)
                updated.add(id);
            else
                updated.delete(id);
            return updated;
        });
    }

    const markNotificationRead = (id: string) => {
        (new NotificationsService()).markNotificationRead(id)
        .then((response) => {
            fetchNotificationList(listRequest);
            props.onForceHeaderRemount();
        }).catch(() => {});
    }

    const applyBulkAction = () => {
        if (!bulkAction || selectedIds.size === 0)
            return;

        const notificationsService = new NotificationsService();
        let action: (id: string) => Promise<boolean>;
        switch (bulkAction) {
            case "read":
                action = (id) => notificationsService.markNotificationRead(id);
                break;
            case "archive":
                action = (id) => notificationsService.markNotificationArchived(id);
                break;
            case "delete":
                action = (id) => notificationsService.deleteNotification(id);
                break;
            default:
                return;
        }

        Promise.all(Array.from(selectedIds).map(id => action(id).catch(() => false)))
            .then(() => {
                setBulkAction("");
                fetchNotificationList(listRequest);
                props.onForceHeaderRemount();
            });
    }

    const deleteNotification = (id: string) => {
        (new NotificationsService()).deleteNotification(id)
        .then((response) => {
            fetchNotificationList(listRequest);
            props.onForceHeaderRemount();
        }).catch(() => {});
    }

    const archiveNotification = (id: string) => {
        // placeholder until the archive API is implemented
    }

    const toggleShowArchived = (event: React.ChangeEvent<HTMLInputElement>) => {
        let checked = event.target.checked;
        setShowArchived(checked);
        fetchNotificationList({
            ...listRequest,
            offset: 0,
            archived: checked
        });
    }

    const toggleShowUnread = (event: React.ChangeEvent<HTMLInputElement>) => {
        let checked = event.target.checked;
        setShowUnread(checked);
        fetchNotificationList({
            ...listRequest,
            offset: 0,
            onlyUnread: checked
        });
    }

    const sortNotificationList = (field: string, direction: "a"|"d") => {
        fetchNotificationList({
            ...listRequest,
            sortField: field,
            sortDirection: direction
        })
    }

    const displayNotificationRow = (item: IXSifterUserNotification) => {
        return (
            <TableRow className={item.hasRead ? "read-notification" : ""}>
                <TableCell>
                    <Checkbox
                        checked={selectedIds.has(item.notificationId)}
                        onChange={(event) => toggleSelectItem(item.notificationId, event.target.checked)}
                    />
                </TableCell>
                <TableCell>{formatIsoDate(item.notificationTimestamp)}</TableCell>
                <TableCell className="message">{item.message}</TableCell>
                <TableCell>
                    {!item.hasRead &&
                    <Tooltip title="mark as read">
                        <IconButton onClick={() => markNotificationRead(item.notificationId)} className="x-hover-blue">
                            <CheckBoxIcon />
                        </IconButton>
                    </Tooltip>
                    }
                </TableCell>
                <TableCell>
                    {!item.isArchived &&
                    <Tooltip title="archive notification">
                        <IconButton onClick={() => archiveNotification(item.notificationId)} className="x-hover-blue">
                            <ArchiveIcon />
                        </IconButton>
                    </Tooltip>
                    }
                </TableCell>
                <TableCell>
                    <Tooltip title="delete notification">
                        <IconButton onClick={() => deleteNotification(item.notificationId)} className="x-hover-red">
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <>
            <Grid container>
                <Grid size={9}>
                    <h1>Notifications</h1>
                </Grid>
                <Grid size={3} sx={{ textAlign: "right" }}>
                    <Button variant='outlined' onClick={() => { navigate("/app")}} sx={{ marginBottom: "16px;", marginTop: "16px"}}>
                        &laquo; back
                    </Button>
                </Grid>
            </Grid>
            <Grid container component="div">
                <Grid size={{ xs: 12 }} component="div">
                    <FormGroup style={{ display: "inline-block" }}>
                        <FormControlLabel control={
                            <Switch checked={showArchived} onChange={toggleShowArchived} />
                        } label="show archived" />
                        <FormControlLabel control={
                            <Switch checked={showUnread} onChange={toggleShowUnread} />
                        } label="show only unread" />
                    </FormGroup>
                </Grid>
            </Grid>
            <div>
                { notificationsList && notificationsList.items.length > 0 &&
                <Grid container component="div">
                    <Grid size={{ xs: 4 }} component="div" style={{ display: "flex", alignItems: "center" }}>
                        <FormControl variant="outlined" size="small" disabled={selectedIds.size === 0} style={{ minWidth: 160, marginRight: "8px" }}>
                            <InputLabel id="bulk-actions-label">Bulk actions</InputLabel>
                            <Select
                                labelId="bulk-actions-label"
                                value={bulkAction}
                                label="Bulk actions"
                                onChange={(event) => setBulkAction(event.target.value as string)}
                            >
                                <MenuItem value="read">Mark as read</MenuItem>
                                <MenuItem value="archive">Archive</MenuItem>
                                <MenuItem value="delete">Delete</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={applyBulkAction} disabled={!bulkAction || selectedIds.size === 0}>
                            Apply
                        </Button>
                    </Grid>
                <Grid size={{ xs: 3 }} component="div" />
                <Grid size={{ xs: 4 }} style={{ textAlign: "right", alignContent: "flex-end"}} component="div">
                    <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} />
                </Grid>
                <Grid size={{ xs: 1 }} component="div" style={{ paddingTop: "8px", whiteSpace: "nowrap"}}>
                    {pluralizeCount(notificationsList?.meta?.count, "notification", "s")}
                </Grid>
                </Grid>
                }
            </div>
            { notificationsList && notificationsList.items.length > 0 
            ? 
                <Table className="x-table-banded notificationList">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ maxWidth: "48px", width: "48px" }}>
                                <Checkbox
                                    checked={notificationsList.items.length > 0 && selectedIds.size === notificationsList.items.length}
                                    indeterminate={selectedIds.size > 0 && selectedIds.size < notificationsList.items.length}
                                    onChange={(event) => toggleSelectAll(event.target.checked)}
                                />
                            </TableCell>
                            <SortableFieldHeader
                              currentDirection={listRequest.sortDirection}
                              currentField={listRequest.sortField}
                              sortField="timestamp"
                              onSort={sortNotificationList}
                            >Timestamp</SortableFieldHeader>
                            <TableCell className="message">Notification</TableCell>
                            <SortableFieldHeader
                              currentDirection={listRequest.sortDirection}
                              currentField={listRequest.sortField}
                              sortField="read"
                              onSort={sortNotificationList}
                            >Unread?</SortableFieldHeader>
                            <TableCell>Archive?</TableCell>
                            <TableCell>Delete?</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notificationsList.items.map(x => displayNotificationRow(x))}
                    </TableBody>
                </Table>
            : <em>No notifications to display</em>
            }    
        </>);        
};

export default XSifterNotifications;